<template>
  <div
    class="border border-solid rounded-sm"
    :class="
      hasOverCapacityWeightClass
        ? 'border-yellow-300'
        : filledWeightClassCount === totalWeightClassCount
          ? 'border-green-300'
          : 'border-red-300'
    "
  >
    <!-- Team Header -->
    <div
      class="flex justify-space-between align-items-center p-sm cursor-pointer"
      :class="[
        expanded ? 'rounded-t-sm' : 'rounded-sm',
        hasOverCapacityWeightClass
          ? 'bg-yellow-50'
          : filledWeightClassCount === totalWeightClassCount
            ? 'bg-green-50'
            : 'bg-red-50',
      ]"
      @click="$emit('toggle-expanded')"
    >
      <!-- Normal header view -->
      <template v-if="!editingName">
        <div class="flex align-items-center gap-x-sm">
          <i
            :class="expanded ? 'fa-solid fa-chevron-down' : 'fa-solid fa-chevron-right'"
          ></i>
          <div>
            <span class="font-semi text-16">{{ displayName }}</span>
            <span
              class="m-l-xs"
              :class="
                filledWeightClassCount === totalWeightClassCount
                  ? 'text-green-900'
                  : 'text-red-900'
              "
            >
              {{ memberCount }} wrestlers ·
              <span
                >{{ filledWeightClassCount }}/{{
                  totalWeightClassCount
                }}
                weights</span
              >
            </span>
          </div>
        </div>
        <div class="flex align-items-center gap-x-sm" @click.stop>
          <button
            class="btn btn-xs btn-default"
            @click="startEditName"
            title="Rename team"
          >
            <i class="fa-solid fa-pencil"></i>
          </button>
          <button
            class="btn btn-xs btn-danger btn-outline"
            @click="confirmDelete"
            :disabled="deleting"
          >
            <spinner v-if="deleting" :inline="true"></spinner>
            <i v-else class="fa-solid fa-trash-can"></i>
          </button>
        </div>
      </template>
      <!-- Editing header view -->
      <div v-else class="flex flex-col gap-y-xs w-full" @click.stop>
        <input
          type="text"
          v-model="editName"
          class="form-control font-no-ios-zoom"
          ref="nameInput"
          @keyup.enter="saveTeamName"
        />
        <div class="flex gap-sm">
          <button class="btn btn-primary" @click="saveTeamName">Save</button>
          <button class="btn btn-default" @click="cancelEditName">
            Cancel
          </button>
        </div>
      </div>
    </div>

    <!-- Finalize banner -->
    <div
      v-if="awaitingApprovalMembers.length > 0 && !editingName"
      class="flex justify-space-between align-items-center p-x-sm p-y-xs bg-yellow-50"
      :class="
        !expanded && canceledMembers.length === 0
          ? 'rounded-b-sm'
          : ''
      "
      @click.stop
    >
      <span class="text-yellow-800 text-14">
        <i class="fa-solid fa-circle-exclamation m-r-xxs"></i>
        {{ awaitingApprovalMembers.length }}
        {{ awaitingApprovalMembers.length === 1
          ? 'wrestler has'
          : 'wrestlers have'
        }} not paid. Finalize team to process payments.
      </span>
      <button
        class="btn btn-xs btn-success flex-shrink-0"
        :disabled="finalizing"
        @click="finalizeTeam"
      >
        <spinner v-if="finalizing" :inline="true"></spinner>
        <span v-else>
          <i class="fa-solid fa-check m-r-xxs"></i>Finalize
        </span>
      </button>
    </div>

    <!-- Payment failed banner -->
    <div
      v-if="canceledMembers.length > 0 && !editingName"
      class="flex justify-space-between align-items-center p-x-sm p-y-xs bg-red-50"
      :class="
        !expanded && overdueMembers.length === 0
          ? 'rounded-b-sm'
          : ''
      "
      @click.stop
    >
      <span class="text-red-800 text-14">
        <i class="fa-solid fa-triangle-exclamation m-r-xxs"></i>
        {{ canceledMembers.length }}
        {{ canceledMembers.length === 1
          ? 'wrestler has'
          : 'wrestlers have'
        }} failed multiple payment attempts.
        Have them update their card, then re-approve
        to retry charging.
      </span>
    </div>

    <!-- Overdue payment banner -->
    <div
      v-if="overdueMembers.length > 0 && !editingName"
      class="flex align-items-center p-x-sm p-y-xs bg-yellow-50"
      :class="expanded ? '' : 'rounded-b-sm'"
      @click.stop
    >
      <span class="text-yellow-800 text-14">
        <i class="fa-regular fa-clock m-r-xxs"></i>
        {{ overdueMembers.length }}
        {{ overdueMembers.length === 1
          ? 'wrestler has'
          : 'wrestlers have'
        }} an overdue payment. Their card will be retried
        automatically. No action needed.
      </span>
    </div>

    <!-- Over capacity banner -->
    <div
      v-if="hasOverCapacityWeightClass && !editingName"
      class="flex align-items-center p-x-sm p-y-xs bg-yellow-50"
      :class="expanded ? '' : 'rounded-b-sm'"
      @click.stop
    >
      <span class="text-yellow-800 text-14">
        <i class="fa-solid fa-triangle-exclamation m-r-xxs"></i>
        Over capacity at {{ overCapacityWeightClassNames.join(', ') }}.
        Remove or reassign wrestlers to meet the limit.
      </span>
    </div>

    <!-- Depth Chart Body -->
    <div v-show="expanded" class="p-sm">
      <div
        v-if="memberCount === 0 && unassignedPoolEntries.length === 0"
        class="text-gray-500 text-center p-y-md"
      >
        No wrestlers assigned to this team yet. Wrestlers will appear as
        suggestions once they register.
      </div>
      <div v-else>
        <!-- Expand/Collapse All -->
        <div class="flex justify-end gap-x-sm m-b-xs">
          <a href="#" class="text-12" @click.prevent="expandAllSections"
            >Expand all</a
          >
          <span class="text-gray-300">|</span>
          <a href="#" class="text-12" @click.prevent="collapseAllSections"
            >Collapse all</a
          >
        </div>

        <!-- Unassigned Section -->
        <div class="m-b-sm border border-solid border-gray-200 rounded-sm">
          <div
            class="flex justify-space-between align-items-center p-sm bg-gray-100 cursor-pointer"
            :class="unassignedExpanded ? 'rounded-t-sm' : 'rounded-sm'"
            @click="unassignedExpanded = !unassignedExpanded"
          >
            <div class="flex align-items-center gap-x-xs">
              <i
                :class="
                  unassignedExpanded ? 'fa-solid fa-chevron-down' : 'fa-solid fa-chevron-right'
                "
              ></i>
              <span class="font-semi">Unassigned</span>
              <span class="text-gray-500 text-12"
                >({{ unassignedWrestlers.length }})</span
              >
            </div>
          </div>
          <div v-show="unassignedExpanded" class="p-sm">
            <div
              v-for="member in unassignedWrestlers"
              :key="memberKey(member)"
              class="m-b-xs"
            >
              <div
                class="grid sm-grid-cols-1 gap-xs md-grid-cols-1 grid-cols-5 p-sm rounded-sm bg-white border border-solid border-gray-200"
              >
                <div class="flex flex-col gap-y-xxs col-span-3">
                  <div>
                    <span
                      :class="memberStatusBadgeClass(member)"
                      >{{ memberStatusLabel(member) }}</span
                    >
                  </div>
                  <div class="flex align-items-center gap-x-sm">
                    <div
                      class="bg-gray-100 rounded-full flex-shrink-0 flex align-items-center"
                      style="padding: 2px"
                    >
                      <img
                        v-if="member.profile.avatar_thumbnail"
                        :src="member.profile.avatar_thumbnail"
                        :alt="member.profile.full_name"
                        class="rounded-full"
                        style="width: 32px; height: 32px; object-fit: cover"
                      />
                      <svg
                        v-else
                        class="text-gray-400"
                        style="width: 32px; height: 32px"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"
                        />
                      </svg>
                    </div>
                    <div>
                      <div class="flex align-items-center gap-x-xs">
                        <span class="font-semi text-gray-900">{{
                          member.profile.full_name
                        }}</span>
                        <span
                          v-if="!isWrestlerEligible(member)"
                          class="text-yellow-600 text-12"
                        >
                          <i
                            class="fa-solid fa-triangle-exclamation m-r-xxs"
                          ></i>
                          {{ getWrestlerIneligibilityReason(member) }}
                        </span>
                      </div>
                      <div class="text-12 text-gray-500">
                        <span v-if="member.profile.weight_class"
                          >{{ member.profile.weight_class }} lbs</span
                        >
                        <span
                          v-if="
                            member.profile.weight_class
                              && member.profile.dob
                          "
                        >
                          /
                        </span>
                        <span v-if="member.profile.dob"
                          >Age {{
                            calculateAge(member.profile.dob)
                          }}</span
                        >
                        <span
                          v-if="
                            member.profile.gender
                              && (member.profile.weight_class
                                || member.profile.dob)
                          "
                        >
                          /
                        </span>
                        <span v-if="member.profile.gender">{{
                          member.profile.gender
                        }}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  class="flex flex-col align-items-center col-span-2 gap-xs w-full"
                >
                  <select
                    class="form-control input-sm"
                    :value="''"
                    @change="
                      handleWeightClassChange(
                        member,
                        $event.target.value,
                      )
                    "
                    @click.stop
                  >
                    <option value="">Set weight class...</option>
                    <option
                      v-for="wc in weightClasses"
                      :key="wc.id"
                      :value="wc.id"
                    >
                      {{ formatWeightClassName(wc) }}
                    </option>
                  </select>
                  <span
                    v-if="isAwaitingApproval(member)"
                    class="flex gap-xxs w-full"
                    @click.stop
                  >
                    <button
                      class="btn btn-sm btn-primary w-full"
                      @click="emitApprovePlan(member)"
                    >
                      Approve
                    </button>
                    <button
                      class="btn btn-sm btn-danger btn-outline w-full"
                      @click="emitDenyPlan(member)"
                    >
                      Deny
                    </button>
                    <button
                      class="btn btn-sm btn-default btn-outline w-full"
                      @click="emitUnassign(member)"
                    >
                      Remove
                    </button>
                  </span>
                  <span
                    v-else-if="isCanceled(member)"
                    class="flex gap-xxs w-full"
                    @click.stop
                  >
                    <button
                      class="btn btn-sm btn-primary w-full"
                      @click="emitApprovePlan(member)"
                    >
                      Re-approve
                    </button>
                    <button
                      class="btn btn-sm btn-default btn-outline w-full"
                      @click="emitUnassign(member)"
                    >
                      Remove
                    </button>
                  </span>
                </div>
              </div>
            </div>
            <div
              v-if="unassignedWrestlers.length === 0"
              class="text-gray-500 text-12 p-sm text-center"
            >
              All wrestlers assigned to weight classes
            </div>
          </div>
        </div>

        <!-- Weight Class Sections -->
        <div
          v-for="wc in weightClasses"
          :key="wc.id"
          :class="[
            'm-b-sm border border-solid rounded-sm',
            isWeightClassOverCapacity(wc.id) ? 'border-red-300' : isWeightClassFull(wc.id) ? 'border-green-300' : 'border-gray-200',
          ]"
        >
          <div
            class="flex flex-wrap justify-space-between align-items-center p-sm cursor-pointer"
            :class="[
              expandedWeightClasses[wc.id] ? 'rounded-t-sm' : 'rounded-sm',
              isWeightClassOverCapacity(wc.id) ? 'bg-yellow-50' : isWeightClassFull(wc.id) ? 'bg-green-50' : 'bg-red-50',
            ]"
            @click="toggleWeightClassExpanded(wc.id)"
          >
            <div class="flex align-items-center gap-x-xs">
              <i
                :class="
                  expandedWeightClasses[wc.id]
                    ? 'fa-solid fa-chevron-down'
                    : 'fa-solid fa-chevron-right'
                "
              ></i>
              <span class="font-semi">
                {{ formatWeightClassName(wc) }}
              </span>
              <template v-if="expandedWeightClasses[wc.id]">
                <span class="text-gray-500 text-12">
                  ({{ getWeightClassCount(wc.id) }})
                </span>
              </template>
              <template v-else-if="maxPerWeightClass === 0">
                <span class="text-gray-500 text-12">
                  ({{ getWeightClassCount(wc.id) }})
                </span>
              </template>
              <template v-else-if="isWeightClassOverCapacity(wc.id)">
                <i
                  class="fa-solid fa-triangle-exclamation text-red-600 text-10"
                  title="Over capacity"
                ></i> <span class="text-heavy text-red-900">Over capacity</span>
              </template>
              <template v-else-if="isWeightClassFull(wc.id)">
                <i class="fa-solid fa-check text-green-600 text-10"></i>
                <i
                  v-if="weightClassHasUnpaid(wc.id)"
                  class="fa-solid fa-triangle-exclamation text-yellow-600 text-10"
                  title="Has wrestlers awaiting payment"
                ></i>
              </template>
              <template
                v-else-if="
                  (startersByWeightClass[wc.id] || []).length > 0 &&
                  maxPerWeightClass > 1
                "
              >
                <span class="text-gray-400 text-10">
                  ({{ (startersByWeightClass[wc.id] || []).length }}/{{
                    maxPerWeightClass
                  }}
                  filled)
                </span>
              </template>
              <template
                v-else-if="(startersByWeightClass[wc.id] || []).length === 0"
              >
                <span class="text-gray-400 text-12 font-italic">
                  0 wrestlers
                </span>
              </template>
              <span
                v-if="getSuggestedForWeightClass(wc.id).length > 0"
                class="text-blue-500 text-10"
              >
                + {{ getSuggestedForWeightClass(wc.id).length }}
                available
              </span>
            </div>
            <div
              v-if="
                !expandedWeightClasses[wc.id] &&
                (wrestlersByWeightClass[wc.id] || []).length > 0
              "
              class="flex justify-end gap-x-xxs"
            >
              <template
                v-for="(m, idx) in (wrestlersByWeightClass[wc.id] || [])"
              >
                <span
                  :key="'name-' + m.profile.id"
                  class="text-14 font-heavy text-gray-700"
                >{{ m.profile.full_name }}<i
                    v-if="isAwaitingApproval(m) || isCanceled(m)"
                    class="fa-solid fa-triangle-exclamation text-10 m-l-xxs"
                    :class="isCanceled(m) ? 'text-red-600' : 'text-yellow-600'"
                  ></i><template
                    v-if="idx < (wrestlersByWeightClass[wc.id] || []).length - 1"
                  >,</template>
                </span>
              </template>
            </div>
          </div>
          <div v-show="expandedWeightClasses[wc.id]" class="p-sm">
            <!-- Assigned wrestlers -->
            <div
              v-for="member in wrestlersByWeightClass[wc.id]"
              :key="memberKey(member)"
              class="m-b-xs"
            >
              <div
                class="grid sm-grid-cols-1 gap-xs md-grid-cols-1 grid-cols-5 p-sm rounded-sm bg-white border border-solid border-gray-200"
              >
                <div class="flex flex-col gap-y-xxs col-span-3">
                  <div>
                    <span
                      :class="memberStatusBadgeClass(member)"
                      >{{ memberStatusLabel(member) }}</span
                    >
                  </div>
                  <div class="flex align-items-center gap-x-sm">
                    <div
                      class="bg-gray-100 rounded-full flex-shrink-0 flex align-items-center"
                      style="padding: 2px"
                    >
                      <img
                        v-if="member.profile.avatar_thumbnail"
                        :src="member.profile.avatar_thumbnail"
                        :alt="member.profile.full_name"
                        class="rounded-full"
                        style="width: 32px; height: 32px; object-fit: cover"
                      />
                      <svg
                        v-else
                        class="text-gray-400"
                        style="width: 32px; height: 32px"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"
                        />
                      </svg>
                    </div>
                    <div>
                      <div>
                        <div class="flex align-items-center gap-x-xs">
                          <span class="font-semi text-gray-900">{{
                            member.profile.full_name
                          }}</span>
                          <span
                            v-if="maxPerWeightClass > 1"
                            class="text-gray-500 text-12 m-l-xxs"
                            >#{{ member.position }}</span
                          >
                          <span
                            v-if="!isWrestlerEligible(member)"
                            class="text-yellow-600 text-12"
                          >
                            <i class="fa-solid fa-triangle-exclamation m-r-xxs"></i>
                            {{ getWrestlerIneligibilityReason(member) }}
                          </span>
                        </div>
                      </div>
                      <div class="text-12 text-gray-500">
                        <span v-if="member.profile.weight_class"
                          >{{ member.profile.weight_class }} lbs</span
                        >
                        <span
                          v-if="
                            member.profile.weight_class && member.profile.dob
                          "
                        >
                          /
                        </span>
                        <span v-if="member.profile.dob"
                          >Age {{ calculateAge(member.profile.dob) }}</span
                        >
                      </div>
                    </div>
                  </div>
                </div>
                <div class="flex flex-col align-items-center col-span-2 gap-xs w-full">
                  <select
                    class="form-control input-sm"
                    :value="member.weight_class_id || ''"
                    @change="
                      handleWeightClassChange(member, $event.target.value)
                    "
                    @click.stop
                  >
                    <option value="">Unassign</option>
                    <option
                      v-for="wcOpt in weightClasses"
                      :key="wcOpt.id"
                      :value="wcOpt.id"
                    >
                      {{ formatWeightClassName(wcOpt) }}
                    </option>
                  </select>
                  <span
                    v-if="isAwaitingApproval(member)"
                    class="flex gap-xxs w-full"
                    @click.stop
                  >
                    <button
                      class="btn btn-sm btn-primary w-full"
                      @click="emitApprovePlan(member)"
                    >
                      Approve
                    </button>
                    <button
                      class="btn btn-sm btn-danger btn-outline w-full"
                      @click="emitDenyPlan(member)"
                    >
                      Deny
                    </button>
                  </span>
                  <span
                    v-else-if="isCanceled(member)"
                    class="flex gap-xxs w-full"
                    @click.stop
                  >
                    <button
                      class="btn btn-sm btn-primary w-full"
                      @click="emitApprovePlan(member)"
                    >
                      Re-approve
                    </button>
                    <button
                      class="btn btn-sm btn-default btn-outline w-full"
                      @click="emitUnassign(member)"
                    >
                      Remove
                    </button>
                  </span>
                </div>
              </div>
            </div>
            <!-- Suggested unassigned wrestlers from pool -->
            <div
              v-for="entry in getSuggestedForWeightClass(wc.id)"
              :key="'suggest-' + entry.id"
              class="m-b-xs"
            >
              <div
                class="flex justify-space-between align-items-center p-sm rounded-sm"
                style="border: 1px dashed #d1d5db; background: #f9fafb"
              >
                <div
                  class="flex align-items-center gap-x-sm"
                  style="opacity: 0.55"
                >
                  <div
                    class="bg-gray-200 rounded-full flex-shrink-0 flex align-items-center"
                    style="padding: 2px"
                  >
                    <svg
                      class="text-gray-300"
                      style="width: 32px; height: 32px"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"
                      />
                    </svg>
                  </div>
                  <div>
                    <span class="font-semi text-gray-500">
                      {{ entry.wrestler_profile.full_name }}
                    </span>
                    <div class="text-12 text-gray-400">
                      <span v-if="entry.wrestler_profile.weight_class">
                        {{ entry.wrestler_profile.weight_class }} lbs
                      </span>
                      <span
                        v-if="
                          entry.wrestler_profile.weight_class &&
                          entry.wrestler_profile.age
                        "
                      >
                        /
                      </span>
                      <span v-if="entry.wrestler_profile.age">
                        Age {{ entry.wrestler_profile.age }}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  class="btn btn-xs"
                  :class="
                    isWeightClassFull(wc.id)
                      ? 'btn-warning btn-outline'
                      : 'btn-primary btn-outline'
                  "
                  style="padding: 1px 8px; font-size: 11px"
                  :disabled="entry.assigning"
                  @click="quickAssign(entry, wc.id)"
                >
                  <spinner v-if="entry.assigning" :inline="true"></spinner>
                  <span v-else-if="isWeightClassFull(wc.id)">
                    <i class="fa-solid fa-right-left m-r-xxs"></i>Swap
                  </span>
                  <span v-else> <i class="fa-solid fa-plus m-r-xxs"></i>Add </span>
                </button>
              </div>
            </div>
            <!-- Cross-team entries -->
            <div
              v-for="entry in getOtherTeamForWeightClass(wc.id)"
              :key="'other-' + entry.id"
              class="m-b-xs"
            >
              <div
                class="flex justify-space-between align-items-center p-sm rounded-sm"
                style="border: 1px dashed #93c5fd; background: #eff6ff"
              >
                <div
                  class="flex align-items-center gap-x-sm"
                  style="opacity: 0.65"
                >
                  <div
                    class="bg-blue-100 rounded-full flex-shrink-0 flex align-items-center"
                    style="padding: 2px"
                  >
                    <svg
                      class="text-blue-300"
                      style="width: 32px; height: 32px"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"
                      />
                    </svg>
                  </div>
                  <div>
                    <span class="font-semi text-blue-700">
                      {{ entry.wrestler_profile.full_name }}
                    </span>
                    <div class="text-12 text-blue-400">
                      {{ entry.assigned_roster.name }}
                    </div>
                  </div>
                </div>
                <button
                  class="btn btn-xs btn-primary btn-outline"
                  style="padding: 1px 8px; font-size: 11px"
                  :disabled="entry.assigning"
                  @click="moveHere(entry, wc.id)"
                >
                  <spinner v-if="entry.assigning" :inline="true"></spinner>
                  <span v-else>
                    <i class="fa-solid fa-arrow-right m-r-xxs"></i>Move here
                  </span>
                </button>
              </div>
            </div>
            <div
              v-if="
                getWeightClassCount(wc.id) === 0 &&
                getSuggestedForWeightClass(wc.id).length === 0 &&
                getOtherTeamForWeightClass(wc.id).length === 0
              "
              class="text-gray-400 text-12 p-sm text-center border border-dashed border-gray-300 rounded-sm"
            >
              No eligible wrestlers in pool
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import swal from 'sweetalert2';
import Spinner from './spinner.vue';
import {
  isEligibleForDivision,
  getIneligibilityReason,
} from './utils/divisionEligibility';

export default {
  name: 'team-registration-team-card',
  components: { Spinner },
  props: {
    roster: {
      type: Object,
      required: true,
    },
    weightClasses: {
      type: Array,
      default: () => [],
    },
    ageDivision: {
      type: Object,
      default: null,
    },
    teamRegistrationDivisionId: {
      type: Number,
      required: true,
    },
    paidSessionId: {
      type: Number,
      required: true,
    },
    paidSessionName: {
      type: String,
      default: '',
    },
    ageDivisionName: {
      type: String,
      default: '',
    },
    teamEntries: {
      type: Array,
      default: () => [],
    },
    expanded: {
      type: Boolean,
      default: false,
    },
    unassignedPoolEntries: {
      type: Array,
      default: () => [],
    },
    maxPerWeightClass: {
      type: Number,
      default: 1,
    },
    otherTeamEntries: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      editingName: false,
      localName: this.roster.name,
      editName: '',
      deleting: false,
      finalizing: false,
      unassignedExpanded: false,
      expandedWeightClasses: {},
    };
  },
  computed: {
    namePrefix() {
      if (!this.paidSessionName || !this.ageDivisionName) return '';
      return `${this.paidSessionName} - ${this.ageDivisionName} `;
    },
    displayName() {
      if (this.namePrefix && this.localName.startsWith(this.namePrefix)) {
        return this.localName.slice(this.namePrefix.length);
      }
      return this.localName;
    },
    allActiveMembers() {
      return this.teamEntries.map((entry) => ({
        pool_entry_id: entry.id,
        profile_id: entry.wrestler_profile.id,
        profile_type: 'WrestlerProfile',
        weight_class_id: entry.weight_class ? entry.weight_class.id : null,
        position: entry.position,
        registration_status: entry.registration_status,
        payment_plan_id: entry.payment_plan_id,
        deposit_paid: entry.deposit_paid || false,
        profile: {
          id: entry.wrestler_profile.id,
          full_name: entry.wrestler_profile.full_name,
          weight_class: entry.wrestler_profile.weight_class,
          avatar_thumbnail: entry.wrestler_profile.avatar_thumbnail,
          dob: entry.wrestler_profile.dob,
          age: entry.wrestler_profile.age,
          gender: entry.wrestler_profile.gender,
          academic_class: entry.wrestler_profile.academic_class,
        },
      }));
    },
    memberCount() {
      return this.allActiveMembers.length;
    },
    unassignedWrestlers() {
      return this.allActiveMembers.filter((m) => !m.weight_class_id);
    },
    wrestlersByWeightClass() {
      const approvedStatuses = ['paid', 'partially_paid', 'pending'];
      const grouped = {};
      for (const wc of this.weightClasses) {
        grouped[wc.id] = this.allActiveMembers
          .filter((m) => m.weight_class_id === wc.id)
          .sort((a, b) => {
            const aApproved = approvedStatuses.includes(a.registration_status)
              ? 0
              : 1;
            const bApproved = approvedStatuses.includes(b.registration_status)
              ? 0
              : 1;
            if (aApproved !== bApproved) return aApproved - bApproved;
            return (a.position || 999) - (b.position || 999);
          });
      }
      return grouped;
    },
    filledWeightClassCount() {
      return this.weightClasses.filter(
        (wc) => (this.wrestlersByWeightClass[wc.id] || []).length > 0,
      ).length;
    },
    hasOverCapacityWeightClass() {
      return this.overCapacityWeightClassCount > 0;
    },
    overCapacityWeightClassCount() {
      return this.overCapacityWeightClassNames.length;
    },
    overCapacityWeightClassNames() {
      return this.weightClasses
        .filter((wc) => this.isWeightClassOverCapacity(wc.id))
        .map((wc) => this.formatWeightClassName(wc));
    },
    totalWeightClassCount() {
      return this.weightClasses.length;
    },
    awaitingApprovalMembers() {
      return this.allActiveMembers.filter((m) => this.isAwaitingApproval(m));
    },
    canceledMembers() {
      return this.allActiveMembers.filter((m) => this.isCanceled(m));
    },
    overdueMembers() {
      return this.allActiveMembers.filter(
        (m) => m.registration_status === 'overdue',
      );
    },
    startersByWeightClass() {
      const grouped = {};
      const max = this.maxPerWeightClass;
      for (const wc of this.weightClasses) {
        const members = this.wrestlersByWeightClass[wc.id] || [];
        if (max > 0) {
          grouped[wc.id] = members.slice(0, max);
        } else {
          grouped[wc.id] = members;
        }
      }
      return grouped;
    },
    suggestedByWeightClass() {
      const grouped = {};
      (this.unassignedPoolEntries || []).forEach((entry) => {
        const wcId = entry.weight_class ? entry.weight_class.id : null;
        if (wcId) {
          if (!grouped[wcId]) grouped[wcId] = [];
          grouped[wcId].push(entry);
        }
      });
      return grouped;
    },
  },
  created() {
    this.unassignedExpanded = this.unassignedWrestlers.length > 0;
    this.expandActiveWeightClasses();
  },
  watch: {
    teamEntries: {
      handler() {
        this.collapseFullWeightClasses();
      },
      deep: true,
    },
  },
  methods: {
    memberKey(member) {
      return `member-${member.profile.id}`;
    },
    startEditName() {
      this.editName = this.displayName;
      this.editingName = true;
      this.$nextTick(() => {
        if (this.$refs.nameInput) {
          this.$refs.nameInput.focus();
        }
      });
    },
    cancelEditName() {
      this.editingName = false;
    },
    buildFullName(shortName) {
      // If the current name had the prefix, reconstruct it; otherwise use as-is
      if (this.namePrefix && this.localName.startsWith(this.namePrefix)) {
        return `${this.namePrefix}${shortName}`;
      }
      return shortName;
    },
    saveTeamName() {
      this.editingName = false;
      const newFullName = this.buildFullName(this.editName);
      if (newFullName !== this.localName) {
        this.localName = newFullName;
        axios
          .put(
            `/api/v1/paid_sessions/${this.paidSessionId}/team_registration_rosters/${this.teamRegistrationDivisionId}`,
            { name: this.localName },
          )
          .then(() => {
            this.$notificationManager.emit(
              'show-toast',
              'Team name updated',
              true,
            );
            this.$emit('name-updated', this.localName);
          })
          .catch(() => {
            this.$notificationManager.emit(
              'show-toast',
              'Failed to update team name',
              false,
            );
            this.localName = this.roster.name;
          });
      }
    },
    // Depth chart methods — single PATCH per action
    handleWeightClassChange(member, newWeightClassId) {
      const vm = this;
      const poolEntryId = member.pool_entry_id;
      if (!poolEntryId) return;

      const params = { pool_entry: {} };
      if (newWeightClassId === null || newWeightClassId === '') {
        params.pool_entry.weight_class_id = null;
        params.pool_entry.position = null;
      } else {
        params.pool_entry.weight_class_id = parseInt(newWeightClassId, 10);
        // Position will be set by the next available slot
        const wcId = parseInt(newWeightClassId, 10);
        const existingInClass = vm.allActiveMembers.filter(
          (m) => m.weight_class_id === wcId && m.profile_id !== member.profile_id,
        );
        params.pool_entry.position = existingInClass.length + 1;
      }

      axios
        .patch(
          `/api/v1/paid_sessions/${vm.paidSessionId}/team_registration_pool_entries/${poolEntryId}`,
          params,
        )
        .then(() => {
          vm.$emit('pool-entry-updated');
        })
        .catch((error) => {
          const msg = error.response?.data?.errors
            ? error.response.data.errors.join(', ')
            : 'Failed to update weight class';
          vm.$notificationManager.emit('show-toast', msg, false);
        });
    },
    getSuggestedForWeightClass(wcId) {
      return this.suggestedByWeightClass[wcId] || [];
    },
    isWeightClassFull(wcId) {
      const max = this.maxPerWeightClass;
      if (!max || max <= 0) return false;
      return (this.wrestlersByWeightClass[wcId] || []).length >= max;
    },
    isWeightClassOverCapacity(wcId) {
      const max = this.maxPerWeightClass;
      if (!max || max <= 0) return false;
      return (this.wrestlersByWeightClass[wcId] || []).length > max;
    },
    quickAssign(entry, wcId) {
      if (this.isWeightClassFull(wcId)) {
        // Swap: displace the last starter
        const starters = this.startersByWeightClass[wcId] || [];
        const displaced = starters[starters.length - 1] || null;
        this.$emit('quick-swap', {
          entry,
          weightClassId: wcId,
          rosterId: this.roster.id,
          displacedMember: displaced,
        });
      } else {
        this.$emit('quick-assign', {
          entry,
          weightClassId: wcId,
          rosterId: this.roster.id,
        });
      }
    },
    moveHere(entry, wcId) {
      if (this.isWeightClassFull(wcId)) {
        const starters = this.startersByWeightClass[wcId] || [];
        const displaced = starters[starters.length - 1] || null;
        this.$emit('quick-swap', {
          entry,
          weightClassId: wcId,
          rosterId: this.roster.id,
          displacedMember: displaced,
        });
      } else {
        this.$emit('quick-assign', {
          entry,
          weightClassId: wcId,
          rosterId: this.roster.id,
        });
      }
    },
    getOtherTeamForWeightClass(wcId) {
      return this.otherTeamEntries[wcId] || [];
    },
    // Eligibility
    isWrestlerEligible(member) {
      if (!this.ageDivision) return true;
      const wrestler = {
        dob: member.profile?.dob,
        academic_class: member.profile?.academic_class,
        gender: member.profile?.gender,
      };
      const competitionYear = new Date().getFullYear();
      return isEligibleForDivision(wrestler, this.ageDivision, competitionYear);
    },
    getWrestlerIneligibilityReason(member) {
      if (!this.ageDivision) return null;
      const wrestler = {
        dob: member.profile?.dob,
        academic_class: member.profile?.academic_class,
        gender: member.profile?.gender,
      };
      const competitionYear = new Date().getFullYear();
      return getIneligibilityReason(
        wrestler,
        this.ageDivision,
        competitionYear,
      );
    },
    // Registration status — read directly from member (pool entry data)
    isAwaitingApproval(member) {
      return member.registration_status === 'awaiting_approval';
    },
    isCanceled(member) {
      return member.registration_status === 'canceled';
    },
    weightClassHasUnpaid(wcId) {
      return (this.startersByWeightClass[wcId] || [])
        .some((m) => this.isAwaitingApproval(m) || this.isCanceled(m));
    },
    memberStatusLabel(member) {
      const s = member.registration_status;
      if (!s) return '';
      if (s === 'awaiting_approval') return member.deposit_paid ? 'Applied (Deposit Paid)' : 'Applied';
      if (s === 'paid' || s === 'partially_paid' || s === 'pending') return 'Approved';
      if (s === 'overdue') return 'Overdue';
      if (s === 'canceled') return 'Payment Failed';
      return '';
    },
    memberStatusBadgeClass(member) {
      const s = member.registration_status;
      if (!s) return '';
      if (s === 'awaiting_approval') return 'badge-yellow';
      if (s === 'paid' || s === 'partially_paid' || s === 'pending') return 'badge-green';
      if (s === 'overdue') return 'badge-red';
      if (s === 'canceled') return 'badge-red';
      return 'badge-gray';
    },
    emitApprovePlan(member) {
      if (member.payment_plan_id) {
        this.$emit('approve-plan', {
          id: member.payment_plan_id,
          approving: false,
          denying: false,
        });
      }
    },
    emitDenyPlan(member) {
      if (member.payment_plan_id) {
        this.$emit('deny-plan', {
          id: member.payment_plan_id,
          approving: false,
          denying: false,
        });
      }
    },
    emitUnassign(member) {
      this.handleWeightClassChange(member, '');
    },
    finalizeTeam() {
      // Collect unique payment plan IDs for all awaiting_approval members
      const planIds = new Set();
      this.awaitingApprovalMembers.forEach((member) => {
        if (member.payment_plan_id) {
          planIds.add(member.payment_plan_id);
        }
      });
      if (planIds.size === 0) return;

      swal
        .fire({
          icon: 'warning',
          title: `Approve ${this.awaitingApprovalMembers.length} wrestler(s)?`,
          text: 'This will charge their registration fees.',
          showCancelButton: true,
          reverseButtons: true,
          confirmButtonText: 'Yes, approve',
          buttonsStyling: false,
          customClass: {
            confirmButton: 'btn btn-success m-l-md',
            cancelButton: 'btn btn-default',
          },
        })
        .then((result) => {
          if (result.value) {
            this.finalizing = true;
            const plans = Array.from(planIds).map((id) => ({
              id,
              approving: false,
              denying: false,
            }));
            this.$emit('finalize-team', plans);
          }
        });
    },
    // Helpers
    calculateAge(dob) {
      if (!dob) return null;
      const today = new Date();
      const birthDate = new Date(dob);
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (
        monthDiff < 0
        || (monthDiff === 0 && today.getDate() < birthDate.getDate())
      ) {
        age--;
      }
      return age;
    },
    formatWeightClassName(weightClass) {
      if (!weightClass) return '';
      return weightClass.display_name || `${weightClass.weight_value} lbs`;
    },
    getWeightClassCount(weightClassId) {
      return (this.wrestlersByWeightClass[weightClassId] || []).length;
    },
    toggleWeightClassExpanded(weightClassId) {
      this.$set(
        this.expandedWeightClasses,
        weightClassId,
        !this.expandedWeightClasses[weightClassId],
      );
    },
    expandActiveWeightClasses() {
      this.expandedWeightClasses = {};
      this.weightClasses.forEach((wc) => {
        const hasSuggested = this.getSuggestedForWeightClass(wc.id).length > 0;
        const hasWrestlers = (this.wrestlersByWeightClass[wc.id] || []).length > 0;
        const full = this.isWeightClassFull(wc.id);
        if (hasSuggested || (hasWrestlers && !full)) {
          this.$set(this.expandedWeightClasses, wc.id, true);
        }
      });
    },
    expandAllWeightClasses() {
      this.expandedWeightClasses = {};
      this.weightClasses.forEach((wc) => {
        this.$set(this.expandedWeightClasses, wc.id, true);
      });
    },
    expandAllSections() {
      this.unassignedExpanded = true;
      this.expandAllWeightClasses();
    },
    collapseAllSections() {
      this.unassignedExpanded = false;
      this.expandedWeightClasses = {};
    },
    collapseFullWeightClasses() {
      if (!this.maxPerWeightClass || this.maxPerWeightClass <= 0) return;
      this.weightClasses.forEach((wc) => {
        if (this.isWeightClassFull(wc.id)) {
          this.$set(this.expandedWeightClasses, wc.id, false);
        }
      });
    },
    confirmDelete() {
      const vm = this;
      swal
        .fire({
          icon: 'warning',
          title: `Delete team "${vm.displayName}"?`,
          text: 'Wrestlers will return to the pool as unassigned. No payments or registrations will be affected.',
          showCancelButton: true,
          reverseButtons: true,
          confirmButtonText: 'Yes, delete',
          buttonsStyling: false,
          customClass: {
            confirmButton: 'btn btn-danger m-l-md',
            cancelButton: 'btn btn-default',
          },
        })
        .then((result) => {
          if (result.value) {
            vm.deleting = true;
            axios
              .delete(
                `/api/v1/paid_sessions/${vm.paidSessionId}/team_registration_rosters/${vm.teamRegistrationDivisionId}`,
              )
              .then(() => {
                vm.$notificationManager.emit(
                  'show-toast',
                  `${vm.localName} deleted`,
                  true,
                );
                vm.$emit('deleted', vm.teamRegistrationDivisionId);
              })
              .catch(() => {
                vm.deleting = false;
                vm.$notificationManager.emit(
                  'show-toast',
                  'Failed to delete team',
                  false,
                );
              });
          }
        });
    },
  },
};
</script>
